import { useEffect, useState } from 'react';
import { Box, MenuItem, Paper, TextField, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import type { PSDGeneral, PSDSieveDetail, PSDHydroDetail } from '../../../types/psd';
import type { SieveNest } from '../../../types/sieveNest';
import { listSieveNests } from '../../../services/sieveNestService';
import { SieveDetailsGrid } from './SieveDetailsGrid';
import { HydroReadingsGrid } from './HydroReadingsGrid';

interface Props {
  general: PSDGeneral;
  sieveDetails: PSDSieveDetail[];
  hydroDetails: PSDHydroDetail[];
  onGeneralChange: (field: keyof PSDGeneral, value: string | number | boolean | null) => void;
  onSieveChange: (next: PSDSieveDetail[]) => void;
  onHydroChange: (next: PSDHydroDetail[]) => void;
  readOnly?: boolean;
}

export const PSDResultEntry = ({
  general, sieveDetails, hydroDetails,
  onGeneralChange, onSieveChange, onHydroChange, readOnly = false,
}: Props) => {
  const theme = useTheme();
  const [nests, setNests] = useState<SieveNest[]>([]);

  useEffect(() => {
    listSieveNests().then(setNests).catch(() => { /* fail silent */ });
  }, []);

  // Nest dropdown: populate the sieve grid LOCALLY (no save required).
  //  - Drop preset rows from the previous nest (IsCustom=false)
  //  - Keep any custom rows the user added (IsCustom=true)
  //  - Append the new nest's catalog rows
  // Backend's syncNestPresetRows() re-applies this on save as defense-in-depth.
  const handleNestChange = (nestKey: string | null) => {
    onGeneralChange('sieveNestKey', nestKey);

    const customRows = sieveDetails.filter((r) => r.isCustom);

    if (!nestKey) {
      onSieveChange(customRows);
      return;
    }

    const nest = nests.find((n) => n.SieveNestKey === nestKey);
    if (!nest) {
      onSieveChange(customRows);
      return;
    }

    const presetRows: PSDSieveDetail[] = [...nest.sizes]
      .sort((a, b) => a.SortOrder - b.SortOrder)
      .map((s) => ({
        sieveSizeMm: s.SieveSizeMm,
        sieveDesignation: s.SieveDesignation,
        sieveDisplayName: s.SieveDisplayName,
        sortOrder: s.SortOrder,
        isCustom: false,
        cumulativeMassRetainedG: null,
        computedSubsamplePassingPct: null,
        computedPassingWholePct: null,
      }));

    onSieveChange([...presetRows, ...customRows]);
  };

  const inputSx = {
    '& .MuiInputBase-input': { py: 0.45, px: 1, fontSize: '0.78rem' },
    '& .MuiInputLabel-root': { fontSize: '0.74rem' },
    '& .MuiInputLabel-shrink': { fontSize: '0.82rem' },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Paper elevation={0} sx={{
        p: 1.5, borderRadius: 2,
        border: `1px solid ${alpha(theme.border?.color ?? '#ccc', 0.35)}`,
        backgroundColor: alpha(theme.palette.background.paper, 0.85),
      }}>
        <Typography variant="overline" sx={{
          fontWeight: 700, color: 'primary.main', fontSize: '0.7rem', letterSpacing: 0.6,
        }}>
          Sieve Nest Selection
        </Typography>
        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', display: 'block', mb: 0.5 }}>
          The selected nest determines which sieves appear in the table below. Manage the catalog at Admin → Sieve Sets.
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1 }}>
          <TextField label="Sieve Nest" select size="small"
            value={general.sieveNestKey ?? ''}
            onChange={(e) => handleNestChange(e.target.value || null)}
            sx={inputSx} disabled={readOnly}>
            <MenuItem value="" sx={{ fontSize: '0.8rem' }}><em>(none)</em></MenuItem>
            {nests.map((n) => (
              <MenuItem key={n.SieveNestKey} value={n.SieveNestKey} sx={{ fontSize: '0.8rem' }}>
                {n.NestName}{n.IsBuiltIn ? '' : ' (custom)'}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Paper>
      <SieveDetailsGrid general={general} rows={sieveDetails} onChange={onSieveChange} readOnly={readOnly} />
      <HydroReadingsGrid general={general} rows={hydroDetails} onChange={onHydroChange} readOnly={readOnly} />
    </Box>
  );
};
